/**
 * "Language is returned to the webapp and echoed on every event. The scan UI is patient-facing, so
 * it localises from the token rather than from a browser guess" (architecture §9). The only input
 * to this module is therefore ScanConfigResponse.language — nothing here ever reads
 * navigator.language, and that omission is deliberate rather than an oversight (see
 * useLanguage.test.tsx, which fails if a browser guess leaks in).
 */
export type SupportedLanguage = 'en' | 'es'

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = ['en', 'es']

/**
 * Each language's name IN THAT LANGUAGE, which is the only way a picker can be read by the person
 * who needs it: a patient who cannot read this page cannot read the word "Spanish" on it either.
 *
 * Not in the translation tables, and not a `TranslationKey`, because these do not vary by the page's
 * current language - "Español" is Español on an English page too.
 */
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
}

export function resolveLanguage(tokenLanguage: string | null): SupportedLanguage {
  const normalized = tokenLanguage?.toLowerCase().slice(0, 2)
  const match = SUPPORTED_LANGUAGES.find((candidate) => candidate === normalized)
  return match ?? 'en'
}

export const translations = {
  en: {
    dateOfBirthPrompt: 'Confirm your date of birth to continue.',
    dateOfBirthLabel: 'Date of birth',
    languageLabel: 'Language',
    continue: 'Continue',
    linkDead: 'This link no longer works.',
    linkDeadDetail: 'It may have expired, already been used, or the details entered did not match.',
    startPrompt: 'Ready to Check Your Vitals?',
    startStepSit: 'Sit down in a comfortable chair.',
    startStepHold: 'Hold your phone so your face is within the outline.',
    startStepCamera: 'Your phone will ask permission to use the camera.',
    startStepStill: 'Sit still and look at the phone for 1 minute.',
    startPrivacy: 'Your privacy is protected. Your video is neither recorded nor saved.',
    startScan: 'Start scan',
    // Shown from the tap until the browser answers for the camera, which is exactly as long as the
    // permission dialog is up. A browser that remembers the answer shows it for no time at all.
    cameraPermission: 'Your phone will ask for permission to use the camera. Tap Allow.',
    // The label on the scanning screen's progress bar, and the only words attached to it. The bar
    // shows its position as a length and renders no digits at all, deliberately
    // (scanning-screen.tsx), so this label is read aloud rather than printed - which is exactly why it
    // still has to be translated. A Spanish page whose only spoken account of its own progress is in
    // English is the failure this table exists to prevent, and it is the one nobody looking at the
    // page would ever notice.
    scanProgress: 'Scan progress',
    // The green line, and the only one (docs/decisions.md D-50). It breaks where the line breaks on
    // the screen; so does the red one the vendor's framing codes raise, `faceCentreDetail`.
    measuringHoldStill: 'Measuring vitals.\nPlease hold still.',
    // The bar is full and the scan is not over: the vendor is finalizing its models and the reading
    // is on its way to the service. Neither reports progress, so this line is what says the wait is
    // part of the scan rather than a screen that has stopped (docs/decisions.md D-50).
    finalizing: 'Finalizing.\nOne moment please.',
    // The one line the second failed scan adds. The heading above it is `scanFailed`, the same
    // sentence the retry screen uses, because it is the same fact - what differs is that this one
    // asks for nothing back, so it says who is dealing with it instead of offering a third attempt.
    careTeamNotified: 'Your care team has been notified.',
    heartRate: 'Heart rate',
    bloodPressure: 'Blood pressure',
    bloodOxygen: 'Blood oxygen',
    breathing: 'Breathing',
    scanComplete: 'Your scan is complete and your results have been sent.',
    scanCompleteReview:
      'Your care team will review them and get in touch if anything needs following up.',
    scanCompleteClose: 'You can close this page now.',
    scanFailed: "We couldn't complete the scan.",
    pageCouldNotLoad: 'This page could not load.',
    tryAgain: 'Try again',

    // Every remaining sentence a patient can be shown. They arrived here from four components and
    // one mapping function, where they were written inline: a page rendering in Spanish with an
    // English paragraph in the middle of it is a failure nothing goes red for.
    dateOfBirthMismatch: "That doesn't match our records. Check the date and try again.",
    tooManyAttemptsTitle: 'Too many attempts',
    tooManyAttemptsDetail: 'Please wait a few minutes and open the link again.',
    scanUnavailable: 'This scan is no longer available.',
    scanInterruptedDetail: 'Connection lost. Reopen your link and try again.',
    scanRestartDetail: 'The scan has to restart.',
    faceLossDetail: "We can't see your face",
    motionDetail: 'Hold still',
    // Mindset's per-frame quality codes (mindset-events.ts). Lighting and face distance are its own
    // codes with no FaceHeart equivalent, so they are its own sentences rather than the nearest
    // existing one - "hold still" is unhelpful, and slightly insulting, to somebody sitting
    // perfectly still in a dark room.
    lightingDetail: 'Move somewhere brighter',
    // Three vendor codes, one line - see mindset-events.ts. The guide drawn over the preview is what
    // "centred" means here, so the words point at it rather than describing a rectangle, and they
    // ask for the phone first: the circle is drawn at the distance those codes stop firing
    // (face-guide-mask.tsx), so a face outside it is usually a face too far away.
    faceCentreDetail: 'Move the phone closer.\nCenter your face in the outline.',
    faceTooFarDetail: 'Move the phone closer',
    faceTooCloseDetail: 'Move the phone further away',

    scanUpdateDetail: 'Keep still and look at the camera',
    scanCouldNotStart: 'We could not start the scan. Please reopen your link and try again.',
    // Its own sentence rather than scanCouldNotStart's, because this one names something the patient
    // can actually do something about: the browser refused the camera, or another app is holding it.
    // "Reopen your link and try again" would be advice that cannot work (docs/decisions.md D-24).
    // Its own sentence, again because the advice differs: this one is a large one-time download that a
    // phone on a weak connection - or one low on memory - can lose. "Reopen your link" is the wrong
    // instruction; Wi-Fi and closing other apps are the right ones (docs/decisions.md D-27).
    scannerCouldNotLoad: 'The scanner could not finish loading. Try Wi-Fi, and close other apps.',
    cameraUnavailable: 'We could not use your camera. Allow access for this page, or close the app using it.',
    noClearReading: 'We weren’t able to get a clear enough reading.',
    couldNotSendResults: 'We couldn’t send your scan results.',
    scanFailedDetail: 'Something went wrong during the scan.',
  },
  es: {
    dateOfBirthPrompt: 'Confirme su fecha de nacimiento para continuar.',
    dateOfBirthLabel: 'Fecha de nacimiento',
    languageLabel: 'Idioma',
    continue: 'Continuar',
    linkDead: 'Este enlace ya no funciona.',
    linkDeadDetail: 'Puede haber caducado, haberse usado ya, o los datos no coincidieron.',
    startPrompt: '¿Listo para revisar sus signos vitales?',
    startStepSit: 'Siéntese en una silla cómoda.',
    startStepHold: 'Sostenga el teléfono de modo que su rostro quede dentro del contorno.',
    startStepCamera: 'Su teléfono le pedirá permiso para usar la cámara.',
    startStepStill: 'Quédese quieto y mire al teléfono durante 1 minuto.',
    startPrivacy: 'Su privacidad está protegida. Su video no se graba ni se guarda.',
    startScan: 'Iniciar escaneo',
    cameraPermission: 'Su teléfono le pedirá permiso para usar la cámara. Toque Permitir.',
    scanProgress: 'Progreso del escaneo',
    measuringHoldStill: 'Midiendo signos vitales.\nQuédese quieto.',
    finalizing: 'Finalizando.\nUn momento, por favor.',
    careTeamNotified: 'Su equipo de atención ha sido informado.',
    heartRate: 'Ritmo cardíaco',
    bloodPressure: 'Presión arterial',
    bloodOxygen: 'Oxígeno en sangre',
    breathing: 'Respiración',
    scanComplete: 'Su escaneo está completo y sus resultados han sido enviados.',
    scanCompleteReview:
      'Su equipo de atención los revisará y se pondrá en contacto si hay algo que requiera seguimiento.',
    scanCompleteClose: 'Ya puede cerrar esta página.',
    scanFailed: 'No pudimos completar el escaneo.',
    pageCouldNotLoad: 'Esta página no pudo cargarse.',
    tryAgain: 'Intentar de nuevo',

    dateOfBirthMismatch: 'Eso no coincide con nuestros registros. Revise la fecha e intente de nuevo.',
    tooManyAttemptsTitle: 'Demasiados intentos',
    tooManyAttemptsDetail: 'Espere unos minutos y vuelva a abrir el enlace.',
    scanUnavailable: 'Este escaneo ya no está disponible.',
    scanInterruptedDetail: 'Conexión perdida. Vuelva a abrir su enlace e intente de nuevo.',
    scanRestartDetail: 'El escaneo debe reiniciarse.',
    faceLossDetail: 'No podemos ver su rostro',
    motionDetail: 'Quédese quieto',
    lightingDetail: 'Busque un lugar más iluminado',
    faceCentreDetail: 'Acerque el teléfono.\nCentre su rostro en el contorno.',
    faceTooFarDetail: 'Acerque el teléfono',
    faceTooCloseDetail: 'Aleje el teléfono',

    scanUpdateDetail: 'Quédese quieto y mire a la cámara',
    scanCouldNotStart: 'No pudimos iniciar el escaneo. Vuelva a abrir su enlace e intente de nuevo.',
    scannerCouldNotLoad: 'El escáner no terminó de cargarse. Pruebe con Wi-Fi y cierre otras aplicaciones.',
    cameraUnavailable: 'No pudimos usar su cámara. Permita el acceso en esta página, o cierre la aplicación que la esté usando.',
    noClearReading: 'No pudimos obtener una lectura suficientemente clara.',
    couldNotSendResults: 'No pudimos enviar los resultados de su escaneo.',
    scanFailedDetail: 'Algo salió mal durante el escaneo.',
  },
} as const satisfies Record<SupportedLanguage, Record<string, string>>

export type TranslationKey = keyof (typeof translations)['en']
